var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/index.tsx
import { action } from "mobx";
import {
  createContext,
  useCallback,
  useContext,
  useEffect
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useMemoOne } from "use-memo-one";
import { Doc, UndoManager, Map as YMap } from "yjs";
import { Fragment, jsx } from "react/jsx-runtime";
var HistoryContext = createContext(null);
function useHistory(required = true) {
  const state = useContext(HistoryContext);
  if (required && state === null)
    throw new Error("useHistory() called outside of HistoryProvider");
  return state;
}
function useWeakHistory() {
  const maybeUndoManager = useContext(HistoryContext);
  const undoManager = useMemoOne(
    // return an UndoManager on an empty YMap so that it doesn't waste resources and can treat its
    // functions like noops
    () => maybeUndoManager != null ? maybeUndoManager : new UndoManager(new Doc().getMap()),
    [maybeUndoManager]
  );
  return undoManager;
}
function HistoryProvider({
  children,
  options = {},
  root
}) {
  const defaultCaptureTransaction = useCallback((transaction) => {
    return transaction.changed.size > 0;
  }, []);
  const undoManager = useMemoOne(
    () => new UndoManager(root, __spreadValues({
      captureTransaction: defaultCaptureTransaction
    }, options)),
    [root]
  );
  useHotkeys(
    "ctrl+z, meta+z",
    action((event) => {
      event.preventDefault();
      undoManager.undo();
    }),
    [undoManager]
  );
  useHotkeys(
    "ctrl+shift+z, meta+shift+z, ctrl+y, meta+y",
    action((event) => {
      event.preventDefault();
      undoManager.redo();
    }),
    [undoManager]
  );
  return /* @__PURE__ */ jsx(HistoryContext.Provider, { value: undoManager, children });
}
function useOverrideNativeUndo(onKeyDown) {
  const history = useHistory(false);
  const undoOrRedo = useCallback(
    (event) => {
      if (history === null)
        return;
      const meta = event.metaKey || event.ctrlKey;
      if (!meta)
        return;
      if (event.key === "z" || event.key === "y") {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "z") {
          if (event.shiftKey) {
            history.redo();
            return;
          }
          history.undo();
          return;
        }
        if (event.key === "y") {
          history.redo();
        }
      }
    },
    [history]
  );
  return useCallback(
    action((event) => {
      undoOrRedo(event);
      onKeyDown == null ? void 0 : onKeyDown(event);
    }),
    [history, onKeyDown, undoOrRedo]
  );
}
function WeakHistoryProvider({ children }) {
  const state = useContext(HistoryContext);
  const root = useMemoOne(() => new YMap(), []);
  if (state === null) {
    return /* @__PURE__ */ jsx(HistoryProvider, { root, children });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function useOnHistoryAdded(callback) {
  const history = useHistory(false);
  useEffect(() => {
    if (!history)
      return;
    history.on("stack-item-added", callback);
    return () => history.off("stack-item-added", callback);
  }, [history, callback]);
}
function useOnHistoryReplay(callback) {
  const history = useHistory(false);
  useEffect(() => {
    if (!history)
      return;
    history.on("stack-item-popped", callback);
    return () => history.off("stack-item-popped", callback);
  }, [history, callback]);
}
function useHistoryState(key, storeState, replayState) {
  const onHistoryAdded = useCallback(
    (event) => {
      event.stackItem.meta.set(key, storeState(event));
    },
    [key, storeState]
  );
  const onHistoryReplay = useCallback(
    (event) => {
      const state = event.stackItem.meta.get(key);
      replayState(state, event);
    },
    [key, replayState]
  );
  useOnHistoryAdded(onHistoryAdded);
  useOnHistoryReplay(onHistoryReplay);
}
export {
  HistoryProvider,
  WeakHistoryProvider,
  useHistory,
  useHistoryState,
  useOnHistoryAdded,
  useOnHistoryReplay,
  useOverrideNativeUndo,
  useWeakHistory
};
